import React from 'react';
import { Wind, Droplets, Flame, UtensilsCrossed, Zap } from 'lucide-react';

const APPLIANCE_META = {
  classic:   { label: 'Classique', icon: UtensilsCrossed, color: 'gray' },
  airfryer:  { label: 'Air Fryer', icon: Wind,            color: 'orange' },
  vapeur:    { label: 'Vapeur',    icon: Droplets,        color: 'blue' },
  four:      { label: 'Four',      icon: Flame,           color: 'red' },
  thermomix: { label: 'Thermomix', icon: Zap,             color: 'purple' },
};

const COLOR_ACTIVE = {
  gray:   'bg-gray-700 text-white',
  orange: 'bg-orange-500 text-white',
  blue:   'bg-blue-500 text-white',
  red:    'bg-red-500 text-white',
  purple: 'bg-purple-500 text-white',
};

const COLOR_IDLE = {
  gray:   'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
  orange: 'bg-white text-orange-600 border-orange-200 hover:bg-orange-50',
  blue:   'bg-white text-blue-600 border-blue-200 hover:bg-blue-50',
  red:    'bg-white text-red-600 border-red-200 hover:bg-red-50',
  purple: 'bg-white text-purple-600 border-purple-200 hover:bg-purple-50',
};

// Appliances always visible even without config (so the convert flow is accessible)
const ALWAYS_VISIBLE = ['airfryer'];

const ApplianceSelector = ({ applianceConfigs = {}, activeMode, onModeChange }) => {
  const configuredKeys = Object.keys(applianceConfigs);

  // Build the full mode list:
  // 1. classic (always first)
  // 2. always-visible appliances (airfryer) even without config
  // 3. other configured keys (hybrids, etc.)
  const modes = ['classic'];
  for (const k of ALWAYS_VISIBLE) {
    if (!modes.includes(k)) modes.push(k);
  }
  for (const k of configuredKeys) {
    if (!modes.includes(k)) modes.push(k);
  }

  return (
    <div className="flex flex-wrap gap-2">
      {modes.map((mode) => {
        const isActive = activeMode === mode;
        const isHybrid = mode.startsWith('hybrid_');
        const hasConfig = mode === 'classic' || Boolean(applianceConfigs[mode]);

        // Determine label and icon
        let label, Icon, color;
        if (isHybrid) {
          const hybridConfig = applianceConfigs[mode];
          label = hybridConfig?.label || mode.replace('hybrid_', '').replace(/_/g, ' + ');
          Icon = Zap;
          color = 'orange';
        } else {
          const meta = APPLIANCE_META[mode];
          label = meta?.label || mode;
          Icon = meta?.icon || UtensilsCrossed;
          color = meta?.color || 'gray';
        }

        const activeClass = COLOR_ACTIVE[color] || COLOR_ACTIVE.gray;
        const idleClass = COLOR_IDLE[color] || COLOR_IDLE.gray;
        const disabledClass = 'bg-gray-100 text-gray-400 border-gray-100 cursor-not-allowed';

        const isClickable = hasConfig || ALWAYS_VISIBLE.includes(mode);

        return (
          <button
            key={mode}
            onClick={() => isClickable && onModeChange(mode)}
            disabled={!isClickable}
            title={!hasConfig && !ALWAYS_VISIBLE.includes(mode) ? `${label} – bientôt disponible` : label}
            className={[
              'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all border',
              isActive ? activeClass : isClickable ? idleClass : disabledClass,
              isHybrid && hasConfig ? 'ring-1 ring-offset-1 ring-orange-300' : ''
            ].join(' ')}
          >
            <Icon className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{label}</span>
            {!hasConfig && !ALWAYS_VISIBLE.includes(mode) && (
              <span className="text-xs opacity-60">(bientôt)</span>
            )}
            {!hasConfig && ALWAYS_VISIBLE.includes(mode) && !isActive && (
              <span className="text-xs opacity-70">+ Convertir</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ApplianceSelector;
